/**
 * Packsize selector — Checkbox per market packsize, mirrors the
 * mock ProvisionalForecastEdit screen at the top of the forecast grid.
 *
 * Drives `selected_packsizes` on the working copy. Plain types (MAS/NC)
 * keep `used_in_calc` mirrored to `selected`; Packout types deselect
 * the calc flag on un-check (the dual-selector for Packout-only saved
 * packs lands in a follow-up task, but the basic toggle works today).
 */

import { observer } from 'mobx-react';
import { Checkbox } from 'primereact/checkbox';

import { useEditStore } from './editStoreContext';
import styles from './packSizeChips.module.scss';

const PackSizeChips = observer(() => {
  const store = useEditStore();
  const slice = store.activeSlice;
  if (!slice) return null;

  // The working copy carries the live selection; fall back to the
  // pristine list if the user hasn't edited yet (lazy clone happens on
  // first toggle).
  const working = store.workingByNDC.get(slice.ndc);
  const selected = (working ?? slice.serverData).selected_packsizes;

  // Universe of packs = market_data for the GFC (everything the
  // operator can toggle). Dedupe by string to match the validator's
  // Decimal-normalised key behaviour without the Decimal dependency.
  const marketPackSizes = Array.from(
    new Set(store.marketData.map(row => row.packsize))
  );

  if (marketPackSizes.length === 0) {
    return (
      <div className={styles.empty}>
        No pack sizes available for this GFC.
      </div>
    );
  }

  // Until the Packout dual-selector ships (task #76), the single chip
  // represents the *calc* set — i.e. whether the pack drives
  // Annual / EU / SU recompute. `selected` follows the chip on plain
  // MAS/NC; on Packout `selected` lingers so a future "saved-only"
  // entry isn't accidentally dropped. Reading `used_in_calc` here
  // keeps the chip visually honest about what the math is using.
  const isOn = (packsize: string): boolean => {
    const entry = selected.find(p => p.packsize === packsize);
    return entry?.used_in_calc ?? false;
  };

  return (
    <div className={styles.wrap}>
      {marketPackSizes.map(packsize => {
        const inputId = `pack-${slice.ndc}-${packsize}`;
        return (
          // `htmlFor` binds the label to the Checkbox's inner input so
          // a click anywhere on the chip (the box OR the label text)
          // dispatches a single, consistent click on the input. Without
          // it, the first click on the visible box can be swallowed —
          // PrimeReact's Checkbox renders both a styled box and a hidden
          // input, and the label's default click-delegation fights the
          // styled box's own handler on the very first render.
          <label key={packsize} htmlFor={inputId} className={styles.chip}>
            <Checkbox
              inputId={inputId}
              checked={isOn(packsize)}
              onChange={() => store.togglePackSelected(slice.ndc, packsize)}
            />
            <span className={styles.label}>{formatPackLabel(packsize)}</span>
          </label>
        );
      })}
    </div>
  );
});

/**
 * Mock displays packsizes via `parseInt(packSize)` — strips the
 * `.0000` decimal noise the backend stores. We keep the same UX so
 * operators see the same labels they're used to.
 */
function formatPackLabel(packsize: string): string {
  const n = Number(packsize);
  if (!Number.isFinite(n)) return packsize;
  // Drop trailing zeros but keep meaningful decimals (e.g. "100.5").
  return Number.isInteger(n) ? String(Math.trunc(n)) : String(n);
}

export default PackSizeChips;
